import React, { useState, useMemo, useCallback } from 'react';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import ProductsTab from '../../components/admin/products/ProductsTab';
import ProductFormModal from '../../components/admin/products/ProductFormModal';
import { useInfiniteProducts, ProductsFilters, useDeleteProduct } from '@/hooks/queries/useProducts';
import { useDebounce } from '@/hooks/useDebounce';
import { Product } from '@/services/apis/products';

const ProductsContainer: React.FC = () => {
  const { t } = useTranslation('admin');

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const filters: ProductsFilters = useMemo(
    () => ({
      limit: 10,
      categoryId: selectedCategoryId === 'all' ? undefined : selectedCategoryId,
      search: debouncedSearch || undefined,
    }),
    [selectedCategoryId, debouncedSearch],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isRefetching, error } =
    useInfiniteProducts(filters);

  const products = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loaderRef = useInfiniteScroll({
    hasMore: !!hasNextPage && !isFetchingNextPage,
    onLoadMore: handleLoadMore,
  });

  const deleteProductMutation = useDeleteProduct();

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedCategoryId(undefined);
  }, []);

  const handleEdit = useCallback((product: Product) => {
    setSelectedProductId(product.id);
    setFormModalOpen(true);
  }, []);

  const handleDelete = useCallback((product: Product) => {
    setSelectedProductId(product.id);
    setDeleteModalOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedProductId(null);
    setFormModalOpen(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedProductId) return;
    try {
      await deleteProductMutation.mutateAsync(selectedProductId);
      addToast({
        title: t('products.actions.deleteSuccess'),
        color: 'success',
      });
      setDeleteModalOpen(false);
      setSelectedProductId(null);
    } catch (error) {
      addToast({
        title: t('products.actions.deleteError'),
        color: 'danger',
      });
    }
  };

  const hasActiveFilters = !!(selectedCategoryId || searchQuery);

  return (
    <>
      <ProductsTab
        products={products}
        isLoading={isLoading || isRefetching}
        isFetchingNextPage={isFetchingNextPage}
        error={error?.message || null}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategoryId={selectedCategoryId}
        onCategoryChange={setSelectedCategoryId}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteModalOpen={deleteModalOpen}
        selectedProduct={selectedProductId ? products.find((p) => p.id === selectedProductId) || null : null}
        onDeleteConfirm={confirmDelete}
        onDeleteModalClose={() => {
          setDeleteModalOpen(false);
          setSelectedProductId(null);
        }}
        isDeleting={deleteProductMutation.isPending}
        loadMoreRef={loaderRef}
        hasNextPage={!!hasNextPage}
        onCreate={handleCreate}
      />

      <ProductFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedProductId(null);
        }}
        productId={selectedProductId}
      />
    </>
  );
};

export default ProductsContainer;
